import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../../components/ui';
import { indentStatusBadge } from '../../../../data/master/assetOwner';
import {
  findBuById,
  findSiteById,
  sitesForBu,
} from '../../../../data/master/businessUnits';
import {
  INDENT_STATUS_OPTIONS,
  WORK_AREA_OPTIONS,
} from '../../../../data/master/indents';
import {
  lookupBusinessUnits,
  lookupSites,
  lookupEquipmentTypes,
} from '../../../../repositories/lookup.repository';

import { listIndents } from '../../../../repositories/indent.repository';
import { getConfigValue } from '../../../../repositories/systemConfig.repository';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';
import { formatDobDisplay } from '../../../../utils/personName';
import { formatIndianDateTimeParts } from '../../../../utils/datetime';
import { formatTimePeriod } from '../../../../utils/timePeriod';
import { IndentFormModal } from './IndentFormModal';

function IndentDateChips({ value }) {
  const parts = formatIndianDateTimeParts(value);
  if (!parts.date && !parts.time) return '—';
  return (
    <Flex gap={1} wrap="nowrap">
      {parts.date ? (
        <StatusBadge status="neutral" fontSize="13px" px={2} py={0.5}>
          {parts.date}
        </StatusBadge>
      ) : null}
      {parts.time ? (
        <StatusBadge status="info" fontSize="13px" px={2} py={0.5}>
          {parts.time}
        </StatusBadge>
      ) : null}
    </Flex>
  );
}

export default function IndentsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const formModal = useDisclosure();
  const [indents, setIndents] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [indenterFormEnabled, setIndenterFormEnabled] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [buFilter, setBuFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [workAreaFilter, setWorkAreaFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [indentRes, buRes, siteRes, typeRes, formEnabled] = await Promise.all([
        listIndents({ pageSize: 200 }),
        lookupBusinessUnits(),
        lookupSites(),
        lookupEquipmentTypes(),
        getConfigValue('indenterFormEnabled'),
      ]);
      setIndents(indentRes.items || []);
      setBusinessUnits(buRes || []);
      setSites(siteRes || []);
      setEquipmentTypes(typeRes || []);
      setIndenterFormEnabled(formEnabled !== false);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load indents',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const vendorOptions = useMemo(() => {
    const map = new Map();
    indents.forEach((ind) => {
      if (!ind.vendorId) return;
      if (!map.has(ind.vendorId)) {
        map.set(ind.vendorId, {
          value: ind.vendorId,
          label: ind.vendorName || ind.vendorId,
          description: ind.vendorId,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [indents]);

  const buOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst || b.buId,
        })),
    [businessUnits],
  );

  const siteOptions = useMemo(
    () =>
      (buFilter
        ? sitesForBu(sites, buFilter)
        : sites.filter((s) => s.active !== false)
      ).map((s) => ({
        value: s.siteId,
        label: s.siteName,
        description: s.projectOperation || s.siteId,
      })),
    [buFilter, sites],
  );

  const equipmentOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({ value: e.name, label: e.name })),
    [equipmentTypes],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return indents.filter((ind) => {
      const matchQ =
        !q ||
        ind.indentId.toLowerCase().includes(q) ||
        ind.line.equipmentType.toLowerCase().includes(q) ||
        (ind.line.specification || '').toLowerCase().includes(q) ||
        (ind.vendorName || '').toLowerCase().includes(q) ||
        (ind.vendorId || '').toLowerCase().includes(q);
      const matchS = !statusFilter || ind.status === statusFilter;
      const matchV = !vendorFilter || ind.vendorId === vendorFilter;
      const matchBu = !buFilter || ind.buId === buFilter;
      const matchSite = !siteFilter || ind.siteId === siteFilter;
      const matchEq = !equipmentFilter || ind.line.equipmentType === equipmentFilter;
      const matchWa = !workAreaFilter || ind.workArea === workAreaFilter;
      return matchQ && matchS && matchV && matchBu && matchSite && matchEq && matchWa;
    });
  }, [
    indents,
    search,
    statusFilter,
    vendorFilter,
    buFilter,
    siteFilter,
    equipmentFilter,
    workAreaFilter,
  ]);

  const pendingCount = useMemo(
    () => indents.filter((i) => (i.status || '') === 'Pending').length,
    [indents],
  );

  const { sorted, sort, toggleSort } = useTableSort(filtered, {
    indentId: (i) => i.indentId,
    createdAt: (i) => i.createdAt || '',
    vendor: (i) => i.vendorName || i.vendorId,
    buSite: (i) =>
      [
        findBuById(businessUnits, i.buId)?.shortName || i.buName || i.buId,
        findSiteById(sites, i.siteId)?.siteName || i.siteName || i.siteId,
      ]
        .filter(Boolean)
        .join(' · '),
    equipment: (i) => i.line.equipmentType,
    period: (i) => i.line.requiredFrom,
    recs: (i) => Number(i.recommendationCount ?? i.recommendations?.length ?? 0),
    status: (i) => i.status,
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setVendorFilter('');
    setBuFilter('');
    setSiteFilter('');
    setEquipmentFilter('');
    setWorkAreaFilter('');
  };

  const refresh = () => load();

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Flex align="center" gap={3} wrap="wrap" mb={1}>
            <Heading size={{ base: 'lg', xl: 'xl' }}>Indents / Requests</Heading>
            <StatusBadge status={pendingCount > 0 ? 'pending' : 'neutral'}>
              {pendingCount} pending
            </StatusBadge>
          </Flex>
          <Text color="text.secondary" mt={2} maxW="720px" fontSize={{ base: '14px', xl: '15px' }}>
            Filter Pending to find requests needing recommendation. Open a row → Find &
            recommend → Assign after offline confirm.
          </Text>
        </Box>
        {indenterFormEnabled ? (
          <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={formModal.onOpen}>
            Indenter Form / Request
          </PrimaryButton>
        ) : null}
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 8 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Indent / vendor / equipment"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Status"
            labelFontSize="13px"
            h="40px"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v ?? '')}
            options={[
              { value: '', label: 'All statuses' },
              ...INDENT_STATUS_OPTIONS,
            ]}
          />
          <AppSelect
            label="Vendor"
            labelFontSize="13px"
            h="40px"
            isSearchable
            searchPlaceholder="Search vendor…"
            value={vendorFilter}
            onChange={(v) => setVendorFilter(v ?? '')}
            options={[{ value: '', label: 'All vendors' }, ...vendorOptions]}
          />
          <AppSelect
            label="Business Unit"
            labelFontSize="13px"
            h="40px"
            isSearchable
            searchPlaceholder="Search BU…"
            value={buFilter}
            onChange={(v) => {
              setBuFilter(v ?? '');
              setSiteFilter('');
            }}
            options={[{ value: '', label: 'All BUs' }, ...buOptions]}
          />
          <AppSelect
            label="Site"
            labelFontSize="13px"
            h="40px"
            isSearchable
            searchPlaceholder="Search site…"
            value={siteFilter}
            onChange={(v) => setSiteFilter(v ?? '')}
            options={[{ value: '', label: 'All sites' }, ...siteOptions]}
          />
          <AppSelect
            label="Equipment"
            labelFontSize="13px"
            h="40px"
            isSearchable
            searchPlaceholder="Search type…"
            value={equipmentFilter}
            onChange={(v) => setEquipmentFilter(v ?? '')}
            options={[{ value: '', label: 'All types' }, ...equipmentOptions]}
          />
          <AppSelect
            label="Work area"
            labelFontSize="13px"
            h="40px"
            value={workAreaFilter}
            onChange={(v) => setWorkAreaFilter(v ?? '')}
            options={[
              { value: '', label: 'All areas' },
              ...WORK_AREA_OPTIONS.map((w) => ({ value: w, label: w })),
            ]}
          />
          <OutlineButton size="sm" w="100%" h="40px" minH="40px" onClick={clearFilters}>
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        {loading ? (
          <TableLoader rows={7} columns={5} />
        ) : (
        <>
        <Box px={{ base: 5, xl: 6 }} pt={{ base: 5, xl: 6 }} pb={3}>
          <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary">
            {totalCount} request{totalCount === 1 ? '' : 's'}
          </Text>
        </Box>
        <Box overflowX="auto">
          <Table
            variant="simple"
            size="sm"
            sx={{
              width: '100%',
              th: {
                px: 2.5,
                py: 2.5,
                whiteSpace: 'nowrap',
                fontSize: '12px',
                width: '1%',
              },
              td: {
                px: 2.5,
                py: 2.5,
                verticalAlign: 'middle',
                width: '1%',
                whiteSpace: 'nowrap',
              },
              'td:nth-of-type(5)': {
                width: 'auto',
                whiteSpace: 'normal',
                minW: '160px',
              },
            }}
          >
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="indentId" sort={sort} onSort={toggleSort}>
                  Indent
                </SortableTh>
                <SortableTh columnKey="createdAt" sort={sort} onSort={toggleSort}>
                  Date of indent
                </SortableTh>
                <SortableTh columnKey="vendor" sort={sort} onSort={toggleSort}>
                  Vendor
                </SortableTh>
                <SortableTh columnKey="buSite" sort={sort} onSort={toggleSort}>
                  BU · Site
                </SortableTh>
                <SortableTh columnKey="equipment" sort={sort} onSort={toggleSort}>
                  Equipment
                </SortableTh>
                <SortableTh columnKey="period" sort={sort} onSort={toggleSort}>
                  Period
                </SortableTh>
                <SortableTh columnKey="recs" sort={sort} onSort={toggleSort}>
                  Recs
                </SortableTh>
                <SortableTh columnKey="status" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
              </Tr>
            </Thead>
            <Tbody>
              {pageItems.map((ind) => {
                const bu = findBuById(businessUnits, ind.buId);
                const site = findSiteById(sites, ind.siteId);
                const periodLabel =
                  formatTimePeriod(ind.line.requiredFrom, ind.line.requiredTill) ||
                  ind.line.timePeriod;
                const recCount = Number(
                  ind.recommendationCount ?? ind.recommendations?.length ?? 0,
                );
                const summary = ind.assignmentSummary;
                const assignedHover =
                  ind.status === 'Assigned' && summary
                    ? [
                        summary.ownerType
                          ? String(summary.ownerType).charAt(0).toUpperCase() +
                            String(summary.ownerType).slice(1)
                          : null,
                        summary.ownerName || null,
                        summary.mobile || null,
                        summary.assetCode || null,
                        summary.regNo || null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : '';
                return (
                  <Tr
                    key={ind.indentId}
                    cursor="pointer"
                    _hover={{ bg: 'bg.input' }}
                    onClick={() => navigate(`/admin/equipments/indents/${ind.indentId}`)}
                  >
                    <Td fontFamily="mono" fontSize="15px" fontWeight="600" whiteSpace="nowrap">
                      {ind.indentId}
                    </Td>
                    <Td whiteSpace="nowrap">
                      <IndentDateChips value={ind.createdAt} />
                    </Td>
                    <Td whiteSpace="nowrap">
                      <Text fontWeight="600" fontSize="15px">
                        {ind.vendorName || ind.vendorId}
                      </Text>
                      <Text fontSize="13px" color="text.secondary">
                        {ind.vendorId}
                      </Text>
                    </Td>
                    <Td fontSize="15px" whiteSpace="nowrap">
                      {[
                        bu?.shortName || ind.buName || ind.buId,
                        site?.siteName || ind.siteName || ind.siteId,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Td>
                    <Td>
                      <Text fontWeight="600" fontSize="15px">
                        {ind.line.equipmentType}
                      </Text>
                      <Text fontSize="13px" color="text.secondary" noOfLines={1}>
                        {ind.line.specification}
                      </Text>
                    </Td>
                    <Td fontSize="15px" whiteSpace="nowrap">
                      <Text>
                        {formatDobDisplay(ind.line.requiredFrom)} →{' '}
                        {formatDobDisplay(ind.line.requiredTill)}
                      </Text>
                      {periodLabel ? (
                        <Text fontSize="13px" color="text.secondary" mt={0.5}>
                          {periodLabel}
                        </Text>
                      ) : null}
                    </Td>
                    <Td fontWeight="600" fontSize="15px" title="Recommendations count">
                      {recCount}
                    </Td>
                    <Td whiteSpace="nowrap" onClick={(e) => e.stopPropagation()}>
                      {assignedHover ? (
                        <Tooltip
                          hasArrow
                          placement="top"
                          label={
                            <VStack align="start" spacing={0.5} py={0.5}>
                              <Text fontSize="12px" fontWeight="700">
                                Given work
                              </Text>
                              {summary?.ownerType ? (
                                <Text fontSize="12px">
                                  {String(summary.ownerType).charAt(0).toUpperCase() +
                                    String(summary.ownerType).slice(1)}
                                  {summary.ownerName ? ` · ${summary.ownerName}` : ''}
                                </Text>
                              ) : summary?.ownerName ? (
                                <Text fontSize="12px">{summary.ownerName}</Text>
                              ) : null}
                              {summary?.mobile ? (
                                <Text fontSize="12px">Mobile · {summary.mobile}</Text>
                              ) : null}
                              {summary?.assetCode || summary?.regNo ? (
                                <Text fontSize="12px" fontFamily="mono">
                                  {[summary.assetCode, summary.regNo].filter(Boolean).join(' · ')}
                                </Text>
                              ) : null}
                            </VStack>
                          }
                        >
                          <Box as="span" display="inline-block">
                            <StatusBadge status={indentStatusBadge(ind.status)} fontSize="13px">
                              {ind.status}
                            </StatusBadge>
                          </Box>
                        </Tooltip>
                      ) : (
                        <StatusBadge status={indentStatusBadge(ind.status)} fontSize="13px">
                          {ind.status}
                        </StatusBadge>
                      )}
                    </Td>
                  </Tr>
                );
              })}
              {pageItems.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <Text color="text.secondary" py={8} textAlign="center" fontSize="15px">
                      No requests match your filters. Try Status → All statuses, or Clear
                      filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
        )}
      </AppCard>

      <IndentFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        onSaved={(indent) => {
          refresh();
          if (indent?.indentId) {
            navigate(`/admin/equipments/indents/${indent.indentId}`);
          }
        }}
      />
    </Box>
  );
}
